import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@fluentui/react';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import {
  ConversionGoalSelector,
  Scopes
} from '@bingads-webui-campaign-react/goal-selector';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { paginationStyle } from '@bingads-webui-campaign-react/common-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useP$DaterangeDefault, ConversionGoalKeyToStringMapping } from '@bingads-webui-campaign-react/wizard-shared-components';

const subTextOptions = [
  {
    id: Scopes.ACCOUNT,
    text: 'Campaign_Goals_Account_Level_Subtext',
  },
  {
    id: Scopes.CAMPAIGN,
    text: 'Campaign_Goals_Campaign_Level_Subtext',
  },
];

const convert2LogData = (data) => {
  const { conversionGoals } = data || {};
  const logData = {
    GoalScopeSelection: conversionGoals.goalScopeSelection,
  };

  if (conversionGoals.goalScopeSelection === Scopes.CAMPAIGN && conversionGoals.goalSelection && conversionGoals.goalSelection.length > 0) {
    logData.GoalSelection = conversionGoals.goalSelection;
  }

  return logData;
};

const ConversionGoalsWithCustomValidation = ({
  i18n, validationProps, updateValidation, ...restProps
}) => {
  const { data: conversionGoalsData = {} } = validationProps;
  const { conversionGoals = {} } = conversionGoalsData || {};

  const classes = useDefaultStyles((_, theme) => ({
    ConversionGoalSelectorContainer: {
      ...paginationStyle(theme),
      '& .multi-selector': {
        width: 952,
      },
      '& .list-selector-container': {
        width: 468,
      },
      '& .goal-selector-subtext': {
        position: 'absolute',
        top: 30,
        left: 35,
      },
      '& .radio:not(:last-child)': {
        marginBottom: 15,
      },
      '& .radio': {
        width: 800,
        '& label': {
          outline: 'none !important',
        },
      },
      '& .radio[data-checked="true"] span': {
        fontWeight: 700,
      },
      '& .goal-selector': {
        marginTop: 25,
      },
      '& .ba-pagination': {
        '& .pager-input': {
          background: '#DCDCDC',
        },
      },
    },
  }));

  const activity = useContext(ActivityContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(
    i18n,
    [ConversionGoalKeyToStringMapping]
  ), [i18n]);

  const $dataRange = useP$DaterangeDefault();

  const onChange = useCallback((changedData) => {
    const newData = {
      conversionGoals: {
        ...conversionGoals,
        ...changedData,
      },
    };
    updateValidation([], newData);

    if (activity) {
      const newLogData = convert2LogData(newData);
      activity.signal(`onConversionGoalChange - ${JSON.stringify(newLogData)}`);
    }
  }, [conversionGoals, updateValidation, activity]);

  const campaignConversionGoal = useMemo(() => ({
    Goals: conversionGoals.originalConversionGoals,
  }), [conversionGoals.originalConversionGoals]);


  return (conversionGoals && conversionGoals.goalScopeSelection ?
    <div className={classes.ConversionGoalSelectorContainer}>
      <div className="conversion-goal-label-container">
        <Text>{i18n.getString(_TL_('How do you want to set your conversion goals'))}</Text>
      </div>
      <ConversionGoalSelector
        {...restProps}
        i18n={legacyI18n}
        isSelectorEnabled
        pageSize={10}
        isMultiMode
        isHideSaveBar
        $dataRange={$dataRange}
        goalSelection={conversionGoals.goalSelection}
        campaignConversionGoal={campaignConversionGoal}
        goalScopeSelection={conversionGoals.goalScopeSelection}
        onChange={onChange}
        activityLogger={activity}
        subTextOptions={subTextOptions}
      />
    </div> : null
  );
};

ConversionGoalsWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      conversionGoals: PropTypes.shape({
        goalSelection: PropTypes.arrayOf(PropTypes.shape({})),
        goalScopeSelection: PropTypes.string,
        originalConversionGoals: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  showTrackingStatusIcon: PropTypes.bool, // included in restProps
};

ConversionGoalsWithCustomValidation.defaultProps = {
  showTrackingStatusIcon: false,
};
export const ValidatedConversionGoals = withError(withCustomValidation(ConversionGoalsWithCustomValidation));
